// V2.1A Step C — Helpers frontend SAL
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const SAL_STATUS_LABELS = {
  bozza: 'Bozza',
  presentato: 'Presentato',
  in_verifica: 'In verifica',
  contestato: 'Contestato',
  approvato: 'Approvato',
  autorizzato_fattura: 'Autorizzato fattura',
  annullato: 'Annullato',
};

export const SAL_STATUS_TONE = {
  bozza: 'bg-slate-100 text-slate-700 border-slate-300',
  presentato: 'bg-blue-100 text-blue-700 border-blue-300',
  in_verifica: 'bg-amber-100 text-amber-800 border-amber-300',
  contestato: 'bg-rose-100 text-rose-800 border-rose-300',
  approvato: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  autorizzato_fattura: 'bg-teal-100 text-teal-800 border-teal-300',
  annullato: 'bg-neutral-200 text-neutral-600 border-neutral-300 line-through',
};

// Stati che concorrono al cumulato autoritativo (deve coincidere col backend)
export const AUTHORITATIVE_STATUSES = new Set(['approvato', 'autorizzato_fattura']);
export const OPEN_STATUSES = new Set(['bozza', 'presentato', 'in_verifica', 'contestato']);

export const formatEuro = (v) => {
  const n = Number(v || 0);
  if (Number.isNaN(n)) return '€ 0,00';
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n);
};

export const formatQty = (v, decimals = 3) => {
  const n = Number(v || 0);
  if (Number.isNaN(n)) return '0';
  return n.toLocaleString('it-IT', {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
};

// App-wide auth is via httpOnly cookie → use withCredentials
export const authHeader = () => ({});
const _wc = { withCredentials: true };

export const salApi = {
  list: (subcontractId) =>
    axios.get(`${API}/api/subcontracts/${subcontractId}/sals`, _wc),
  availability: (subcontractId) =>
    axios.get(`${API}/api/subcontracts/${subcontractId}/sals/availability`, _wc),
  create: (subcontractId, payload) =>
    axios.post(`${API}/api/subcontracts/${subcontractId}/sals`, payload, _wc),
  get: (salId) =>
    axios.get(`${API}/api/subcontract-sals/${salId}`, _wc),
  update: (salId, payload) =>
    axios.put(`${API}/api/subcontract-sals/${salId}`, payload, _wc),
  transition: (salId, payload) =>
    axios.post(`${API}/api/subcontract-sals/${salId}/transition`, payload, _wc),
  itemVerification: (salId, itemId, payload) =>
    axios.patch(
      `${API}/api/subcontract-sals/${salId}/items/${itemId}/verification`,
      payload,
      _wc,
    ),
  preview: (subcontractId, payload) =>
    axios.post(`${API}/api/subcontracts/${subcontractId}/sals/preview`, payload, _wc),

  // --- V2.1A Step D · Documenti ---
  generatePdf: (salId) =>
    axios.post(
      `${API}/api/subcontract-sals/${salId}/generate-pdf`,
      null,
      { ..._wc, responseType: 'blob' },
    ),
  listDocuments: (salId) =>
    axios.get(`${API}/api/subcontract-sals/${salId}/documents`, _wc),
  uploadDocument: (salId, file, documentType, notes) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(
      `${API}/api/subcontract-sals/${salId}/documents`,
      fd,
      {
        ..._wc,
        params: { document_type: documentType, notes: notes || undefined },
      },
    );
  },
  uploadSignedPdf: (salId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(
      `${API}/api/subcontract-sals/${salId}/upload-signed-pdf`,
      fd,
      _wc,
    );
  },
  getSignedActive: (salId) =>
    axios.get(`${API}/api/subcontract-sals/${salId}/signed-pdf/active`, _wc),
  downloadDocument: (salId, documentId) =>
    axios.get(
      `${API}/api/subcontract-sals/${salId}/documents/${documentId}/download`,
      { ..._wc, responseType: 'blob' },
    ),
};

/**
 * Utility: forza il download del blob come attachment via anchor click.
 * Rilascia l'object URL a fine ciclo per evitare leak.
 */
export const triggerBlobDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'documento';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Rilascio in coda micro-task per compatibilità Safari
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

/** Ricava filename da Content-Disposition (RFC 6266, fallback semplice). */
export const filenameFromContentDisposition = (cd, fallback = 'documento') => {
  if (!cd || typeof cd !== 'string') return fallback;
  const m = cd.match(/filename\*?=(?:UTF-8''|")?([^";\r\n]+)"?/i);
  if (!m) return fallback;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
};

/**
 * Aggregati progresso cumulativo runtime lato client (dai SAL già scaricati).
 * Coerente con la regola AUTHORITATIVE_STATUSES.
 */
export const computeSubcontractSalSummary = (sals, contractTotal) => {
  const s = { matured: 0, retention: 0, authorized: 0, contract_total: Number(contractTotal || 0) };
  (sals || []).forEach((sal) => {
    if (AUTHORITATIVE_STATUSES.has(sal.status)) {
      s.matured += Number(sal.gross_approved_amount || 0);
      s.retention += Number(sal.retention_amount || 0);
    }
    if (sal.status === 'autorizzato_fattura') {
      s.authorized += Number(sal.authorized_net_amount || 0);
    }
  });
  s.remaining = Math.max(0, s.contract_total - s.matured);
  s.matured_percent = s.contract_total > 0 ? (s.matured / s.contract_total) * 100 : 0;
  s.authorized_percent = s.contract_total > 0 ? (s.authorized / s.contract_total) * 100 : 0;
  s.remaining_percent = s.contract_total > 0 ? (s.remaining / s.contract_total) * 100 : 0;
  s.overrun = s.matured > s.contract_total;
  return s;
};

export const extractApiError = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return { message: d };
  if (d && typeof d === 'object') return { message: d.message || 'Errore', code: d.code, details: d.details };
  if (err?.response?.status === 404) return { message: 'Funzionalità SAL non disponibile.' };
  if (err?.response?.status === 403) return { message: 'Permesso mancante.' };
  return { message: err?.message || 'Errore imprevisto' };
};
